import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { forkJoin, of } from 'rxjs';
// ↳ Adjust the import path / service name to match your project structure
import { ApiCallService } from '@app/services/api-call.service';
import { ChangeDetectorRef } from '@angular/core';
import { ConfigService } from '@app/shared/services/config.service';



interface AdmitCardDialogData {
  candidateId: string;
  examId: string;
  selectedCustomAttributes?: string[];
  autoDownload?: boolean;
}

@Component({
    selector: 'app-admit-card',
    templateUrl: './admit-card.component.html',
    styleUrls: ['./admit-card.component.css'],
    standalone: false
})
export class AdmitCardComponent implements OnInit {
  private readonly pdfBrandColor = '#34495e';
  /** Template reference to the block we want to convert to PDF */
  @ViewChild('pdfTable', { static: false }) pdfTable!: ElementRef<HTMLElement>;

  /** API data */
  admitCard: any;
  organizationName!: string;
  organizationAddress!: string;
  candidateDetails: any;
  exams: any;
  examUrl: string;
  selectedCustomAttributes: string[] = [];
  autoDownload = false;
  instructions: string[] = [
    'Read all instructions carefully before starting the examination.',
    'Use only blue or black pen for writing. Pencil writing is not allowed.',
    'Do not make any marks or writing outside the designated areas.',
    'Follow the examination timings strictly as mentioned in the admit card.',
    'Be present at the examination center 15 minutes before the scheduled time.',
    'Maintain silence and discipline in the examination hall.',
  ];
  
  /** UI state */
  isLoading = true;

  constructor(
    public dialogRef: MatDialogRef<AdmitCardComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AdmitCardDialogData,
    private apiCallService: ApiCallService,
    private config: ConfigService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    this.examUrl = this.config.candidatePortalURL;
  }

  /** Fetch admit‑card details and candidate information */
  ngOnInit(): void {
    console.log('Dialog data received:', this.data);
    console.log('Candidate ID:', this.data?.candidateId);
    this.selectedCustomAttributes = this.data?.selectedCustomAttributes || [];
    this.autoDownload = this.data?.autoDownload || false;
    // Prepare API calls
    const admitCardCall = this.apiCallService.getadmitcard(this.data);
    
    // Only fetch candidate data if candidateId is provided and not undefined
    const candidateCall = this.data?.candidateId && this.data.candidateId !== 'undefined' 
      ? this.apiCallService.getCandidateById(this.data.candidateId)
      : of(null);

    // Fetch both admit card and candidate details in parallel
    forkJoin([admitCardCall, candidateCall]).subscribe({
      next: ([admitCardRes, candidateRes]: any[]) => {
        // Process Admit Card Data
        const admitCardPayload = admitCardRes?.body?.responseBody?.admitCardDetails || [];
        console.log('Admit card data:', admitCardPayload);
        this.exams = admitCardPayload;

        // Extract candidate details from the first exam object (contains login_id, full_name, username, email, etc.)
        if (this.exams && Array.isArray(this.exams) && this.exams.length > 0) {
          this.candidateDetails = this.exams[0];
          console.log('Candidate details extracted:', this.candidateDetails);
        }

        // Format the dates for each exam
        if (this.exams && Array.isArray(this.exams)) {
          this.exams.forEach((exam) => {
            exam.start_datetime = this.formatDate(exam.start_datetime);
            exam.end_datetime = this.formatDate(exam.end_datetime);
            // Sanitize instructions HTML
            if (exam.instructions) {
              exam.instructions = this.sanitizer.bypassSecurityTrustHtml(exam.instructions);
            }
          });
        }

        console.log('Updated exam card data:', this.exams);
        
        // Bind admit card to template
        this.admitCard = admitCardPayload[0]?.admit_card;
        this.organizationName = admitCardPayload[0]?.organization_name;
        this.organizationAddress = admitCardPayload[0]?.organization_address;

        // Process additional Candidate Data - only if candidateRes is provided separately
        if (candidateRes) {
          const candidatePayload = candidateRes?.body?.responseBody || candidateRes?.body?.responseBody?.[0] || {};
          console.log('Additional candidate data:', candidatePayload);
          // Merge with existing candidateDetails to get any additional fields
          this.candidateDetails = { ...this.candidateDetails, ...candidatePayload };
        }

        this.isLoading = false;
        if (this.autoDownload) {
          setTimeout(() => {
            this.download();
            setTimeout(() => {
              this.dialogRef.close();
            }, 500);
          }, 300);
        }
      },
      error: (err) => {
        console.error('Failed to fetch data:', err);
        this.isLoading = false;
      },
    });
  }

  // Method to format date
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString().slice(0, 19).replace('T', ' ');
  }

  // Helper method to get custom attribute value by label
  getCustomAttributeValue(label: string): string | null {
    if (!this.candidateDetails?.custom_attrs || !Array.isArray(this.candidateDetails.custom_attrs)) {
      return null;
    }
    const attr = this.candidateDetails.custom_attrs.find(
      (a: any) => a.label && a.label.toLowerCase() === label.toLowerCase()
    );
    return attr?.value || null;
  }

  getFilteredCustomAttributes(): any[] {
    if (!this.candidateDetails?.custom_attrs || !Array.isArray(this.candidateDetails.custom_attrs)) {
      return [];
    }
    
    const excludeLabels = ['roll number', 'registration id'];
    if (!this.autoDownload) {
      return this.candidateDetails.custom_attrs.filter((attr: any) => {
        const labelLower = attr.label ? attr.label.toLowerCase() : '';
        return !excludeLabels.includes(labelLower);
      });
    }

    if (!this.selectedCustomAttributes || this.selectedCustomAttributes.length === 0) {
      return [];
    }
    
    const selectedLabels = this.selectedCustomAttributes.map((attr: any) => {
      const labelStr = typeof attr === 'string' ? attr : (attr.label || attr.key || '');
      return labelStr.toLowerCase().trim();
    });
    
    return this.candidateDetails.custom_attrs.filter((attr: any) => {
      const labelLower = (attr.label || attr.key || '').toLowerCase().trim();
      return !excludeLabels.includes(labelLower) && selectedLabels.includes(labelLower);
    });
  }

  getExamCustomAttributes(exam: any): any[] {
    if (!exam?.custom_attrs || !Array.isArray(exam.custom_attrs)) {
      return [];
    }
    
    if (!this.autoDownload) {
      return exam.custom_attrs;
    }

    if (!this.selectedCustomAttributes || this.selectedCustomAttributes.length === 0) {
      return [];
    }

    const selectedLabels = this.selectedCustomAttributes.map((attr: any) => {
      const labelStr = typeof attr === 'string' ? attr : (attr.label || attr.key || '');
      return labelStr.toLowerCase().trim();
    });

    return exam.custom_attrs.filter((attr: any) => {
      const labelLower = (attr.label || attr.key || '').toLowerCase().trim();
      return selectedLabels.includes(labelLower);
    });
  }
  // Format exam date (show only date)
  formatExamDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  // Format exam time (show only time)
  formatExamTime(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    return date.toLocaleTimeString('en-US', options);
  }


  toggleShowFullLink(exam: any) {
    exam.showFullLink = !exam.showFullLink;
  }

  // Check if the login mode is login_id (vs access_url)
  isLoginIdMode(exam: any): boolean {
    // Check if rules contains loginMode set to login_id
    // Default to access_url if not specified (maintain backward compatibility)
    const loginMode = exam?.rules?.loginMode || exam?.login_mode || 'access_url';
    console.log('Login Mode for exam:', exam?.title, '=', loginMode);
    return loginMode === 'login_id';
  }

  // Get the login ID from candidate data
  getLoginId(): string {
    if (!this.candidateDetails) return '';
    const loginId = (this.candidateDetails.login_id || 
                    this.candidateDetails.username || 
                    this.candidateDetails.email || '').toUpperCase();
    console.log('Login ID:', loginId);
    return loginId;
  }
  
  /** Generate a multi‑page A4 PDF of the admit‑card **/
  private addPdfBranding(
      pdf: jsPDF,
      downloadedAt: string,
      pageNo: number,
      totalPages: number
    ): void {
      if (pageNo !== totalPages) {
        return; 
      }

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Line drawing configuration
      pdf.setDrawColor(this.pdfBrandColor);
      pdf.setLineWidth(0.2);
      
      pdf.line(10, pageHeight - 11, pageWidth - 10, pageHeight - 11);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(this.pdfBrandColor);
      pdf.text(`Downloaded on: ${downloadedAt}`, 10, pageHeight - 6);
      pdf.text('Powered by Certiplate', pageWidth / 2, pageHeight - 6, {
        align: 'center',
      });
      pdf.text(`Page ${pageNo} of ${totalPages}`, pageWidth - 10, pageHeight - 6, {
        align: 'right',
      });
    }

  download(): void {
    const div = this.pdfTable.nativeElement;
    const originalStyle = {
      width: div.style.width,
      height: div.style.height,
      overflow: div.style.overflow,
      display: div.style.display,
    };

    div.style.width = '800px'; 
    div.style.height = 'auto';
    div.style.overflow = 'visible';
    div.style.display = 'block';

    html2canvas(div, {
      scale: 2, 
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollY: -window.scrollY 
    })
      .then((canvas) => {
        Object.assign(div.style, originalStyle);

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        
        const sideMargin = 10;
        const headerHeight = 20; 
        const footerHeight = 14;
        const contentWidth = pageWidth - sideMargin * 2;
        
        const contentHeight = pageHeight - headerHeight - footerHeight;
        const scaledImageHeight = (canvas.height * contentWidth) / canvas.width;
        const totalPages = Math.ceil(scaledImageHeight / contentHeight);
        const downloadedAt = new Date().toLocaleString();

        let yOffset = 0;
        let pageNo = 1;

        while (yOffset < scaledImageHeight) {
          if (pageNo > 1) {
            pdf.addPage();
          }

          pdf.addImage(
            imgData,
            'PNG',
            sideMargin,
            headerHeight - yOffset,
            contentWidth,
            scaledImageHeight,
            undefined,
            'FAST'
          );

          if (pageNo > 1) {
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pageWidth, headerHeight, 'F');
          }

          this.addPdfBranding(pdf, downloadedAt, pageNo, totalPages);
          yOffset += contentHeight;
          pageNo += 1;
        }

        pdf.save(`AdmitCard_${new Date().toISOString()}.pdf`);
      })
      .catch((error) => {
        console.error('Error generating admit card PDF:', error);
        Object.assign(div.style, originalStyle);
      });
  }

  generatePdfAsBase64(): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        const div = this.pdfTable.nativeElement;
        const originalStyle = {
          width: div.style.width,
          height: div.style.height,
          overflow: div.style.overflow,
          display: div.style.display,
        };

        div.style.width = '800px'; 
        div.style.height = 'auto';
        div.style.overflow = 'visible';
        div.style.display = 'block';

        html2canvas(div, {
          scale: 2, 
          useCORS: true,
          allowTaint: true,
          logging: false,
          scrollY: -window.scrollY 
        })
          .then((canvas) => {
            Object.assign(div.style, originalStyle);

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            
            const sideMargin = 10;
            const headerHeight = 20; 
            const footerHeight = 14;
            const contentWidth = pageWidth - sideMargin * 2;
            
            const contentHeight = pageHeight - headerHeight - footerHeight;
            const scaledImageHeight = (canvas.height * contentWidth) / canvas.width;
            const totalPages = Math.ceil(scaledImageHeight / contentHeight);
            const downloadedAt = new Date().toLocaleString();

            let yOffset = 0;
            let pageNo = 1;

            while (yOffset < scaledImageHeight) {
              if (pageNo > 1) {
                pdf.addPage();
              }

              pdf.addImage(
                imgData,
                'PNG',
                sideMargin,
                headerHeight - yOffset,
                contentWidth,
                scaledImageHeight,
                undefined,
                'FAST'
              );

              if (pageNo > 1) {
                pdf.setFillColor(255, 255, 255);
                pdf.rect(0, 0, pageWidth, headerHeight, 'F');
              }

              this.addPdfBranding(pdf, downloadedAt, pageNo, totalPages);
              yOffset += contentHeight;
              pageNo += 1;
            }

            const pdfData = pdf.output('dataurlstring');
            const base64String = pdfData.split(',')[1];
            resolve(base64String);
          })
          .catch((error) => {
            console.error('Error generating PDF as base64:', error);
            Object.assign(div.style, originalStyle);
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  }
}
  
